// "Paid from savings": a purchase covered by money moved back from a fund (a set_aside category).
// The same choice in the edit and add sheets (a hidden field, read on save) and on each New
// purchases row (main.ts ui.newFunds). Quiet until used: a link, plus "Paid from Travel again?" when
// the merchant was paid from a fund last time (offered, never picked by itself). Copy in shared/savings.ts.
import type { CategoryDto, OverviewDto, TransactionDto } from '../../../shared/api';
import { FUND_LABEL, FUND_NONE, fundAgain, fundPaidLine, fundUsedLine } from '../../../shared/savings';
import { esc } from '../format';

/** The household's funds: its set_aside categories. */
export const funds = (d: OverviewDto) => d.categories.filter((k) => k.kind === 'set_aside');

const fundName = (d: OverviewDto, id: string | null) => (id ? (d.categories.find((k) => k.id === id && k.kind === 'set_aside')?.name ?? null) : null);

/** My own spending only: never a Done entry, money set aside or a yearly charge spread over 12 months. */
export function canFund(d: OverviewDto, t: TransactionDto | null, categoryId: string | null = t?.categoryId ?? null): boolean {
  if (!funds(d).length) return false;
  if (t && (t.source === 'transfer' || t.spread)) return false;
  return !(categoryId && funds(d).some((k) => k.id === categoryId));
}

/**
 * The choice itself. `picked`: the fund chosen ('' none); `open`: the chips show (else a link).
 * `act` names the actions: the sheet's, or a row's (with its data-id).
 */
export function fundChoice(d: OverviewDto, o: { picked: string; open: boolean; usual: string | null; act: (name: 'open' | 'pick', fund?: string) => string }): string {
  const name = fundName(d, o.picked || null);
  const usual = !o.picked ? fundName(d, o.usual) : null;
  // The merchant's usual fund: one tap, never picked by itself.
  const again = usual ? `<button class="linkbtn fundagain" ${o.act('pick', o.usual!)}>${esc(fundAgain(usual))}</button>` : '';
  if (!o.open && !name) return `<div class="fundrow"><button class="linkbtn" ${o.act('open')}>${FUND_LABEL}</button>${again}</div>`;
  const chips = [{ id: '', name: FUND_NONE }, ...funds(d)]
    .map((k) => `<button class="chip" ${o.act('pick', k.id)} aria-pressed="${k.id === o.picked}">${esc(k.name)}</button>`)
    .join('');
  return `<div class="fld"><span>${FUND_LABEL}</span><div class="chips">${chips}</div></div>${again}
  ${name ? `<p class="hint fundline">${esc(fundPaidLine(name))}</p>` : ''}`;
}

/** The edit or add sheet's choice: its state in #f-fund (the fund's id, '' for none). */
export function sheetFundBlock(d: OverviewDto, t: TransactionDto | null, picked: string, open = !!picked): string {
  return `<div class="fundbox" id="f-fund-box"><input type="hidden" id="f-fund" value="${esc(picked)}">${fundChoice(d, {
    picked,
    open,
    usual: t?.usualFundId ?? null,
    act: (name, fund) => (name === 'open' ? 'data-a="fund-open"' : `data-a="fund-pick" data-fund="${esc(fund ?? '')}"`),
  })}</div>`;
}

/** A New purchases row's choice (ui.newFunds: absent closed, '' open with none picked). */
export function newFundBlock(d: OverviewDto, t: TransactionDto, picked: string | undefined): string {
  if (!canFund(d, t)) return '';
  const id = esc(t.id);
  return `<div class="fundbox newfund">${fundChoice(d, {
    picked: picked ?? '',
    open: picked !== undefined,
    usual: t.usualFundId,
    act: (name, fund) => (name === 'open' ? `data-a="new-fund-open" data-id="${id}"` : `data-a="new-fund-pick" data-id="${id}" data-fund="${esc(fund ?? '')}"`),
  })}</div>`;
}

/** On a fund's own screen: "Used this month: $120.00", only when something was. A tally, not a balance. */
export const fundUsedBlock = (d: OverviewDto, k: CategoryDto) =>
  d.fundUsed?.[k.id] ? `<p class="hint fundused">${esc(fundUsedLine(d.fundUsed[k.id]))}</p>` : '';
